import { useCallback, useEffect, useMemo, useState } from "react";

const KEY = "logout:reason";

const logoutReasons = ["idle", "expired", "revoked", "manual", "fraud"] as const;
export type LogoutReason = (typeof logoutReasons)[number];

const ALERT_REASONS: LogoutReason[] = ["idle", "expired", "fraud"];

const REASON_MESSAGES: Record<LogoutReason, string> = {
  idle: "Your session has expired, you were logged out to keep your account secure. Please sign in again to continue.",
  expired:
    "Your session has expired, you were logged out to keep your account secure. Please sign in again to continue.",
  revoked: "Your session was revoked. Please log in again.",
  manual: "You have been logged out.",
  fraud: "Your account has been restricted. Please contact support for assistance.",
};

export function useLogoutReason(options: { consumeReason: boolean } = { consumeReason: false }) {
  const [logoutReason, setLogoutReasonState] = useState<LogoutReason | null>(null);
  const { consumeReason } = options;

  const setLogoutReason = useCallback((reason: LogoutReason) => {
    sessionStorage.setItem(KEY, reason);
  }, []);

  useEffect(() => {
    if (!consumeReason) return;
    const value = sessionStorage.getItem(KEY) as LogoutReason | null;
    if (!value) return;

    if (!logoutReasons.includes(value)) {
      sessionStorage.removeItem(KEY);
      return;
    }

    sessionStorage.removeItem(KEY);

    if (ALERT_REASONS.includes(value)) {
      setLogoutReasonState(value);
    }
  }, [consumeReason]);

  const logoutMessage = useMemo(() => {
    if (!logoutReason) return null;
    return REASON_MESSAGES[logoutReason];
  }, [logoutReason]);

  return { logoutReason, logoutMessage, setLogoutReason };
}
